import { Check } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../i18n/LanguageContext";

export const Pricing = () => {
  const { t } = useLanguage();

  return (
    <section id="preturi" className="bg-white py-20 lg:py-28" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="section-label" data-testid="pricing-label">{t.pricing.label}</p>
          <h2
            className="font-display mt-4 text-3xl font-bold tracking-tight text-[#0d2b52] sm:text-4xl"
            data-testid="pricing-title"
          >
            {t.pricing.title}
          </h2>
          <p className="mt-4 text-base text-[#475569] sm:text-lg" data-testid="pricing-subtitle">
            {t.pricing.subtitle}
          </p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {t.pricing.plans.map((plan, i) => {
            const isPopular = Boolean(plan.popular);
            return (
              <article
                key={i}
                className={`relative flex flex-col border p-8 transition-all duration-300 hover:-translate-y-1 ${
                  isPopular
                    ? "border-[#1d6fd1] bg-[#0d2b52] text-white"
                    : "border-[#e2e8f0] bg-white hover:border-[#1d6fd1]"
                }`}
                data-testid={`pricing-plan-${i}`}
              >
                {isPopular && (
                  <span
                    className="font-display absolute -top-3.5 left-8 bg-[#1d6fd1] px-3 py-1 text-xs font-bold uppercase tracking-widest text-white"
                    data-testid="pricing-popular-badge"
                  >
                    {plan.popular}
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold">{plan.name}</h3>
                <p className={`mt-2 text-sm ${isPopular ? "text-[#cbd5e1]" : "text-[#64748b]"}`}>
                  {plan.desc}
                </p>
                {plan.price ? (
                  <p className="mt-6 flex flex-wrap items-baseline gap-x-1.5" data-testid={`pricing-price-${i}`}>
                    {plan.price.prefix && (
                      <span className={`text-sm font-semibold ${isPopular ? "text-[#cbd5e1]" : "text-[#64748b]"}`}>
                        {plan.price.prefix}
                      </span>
                    )}
                    <span className={`font-display text-4xl font-bold tracking-tight ${isPopular ? "text-white" : "text-[#0d2b52]"}`}>
                      {plan.price.amount}
                    </span>
                    <span className={`font-display text-lg font-bold ${isPopular ? "text-[#2f9be8]" : "text-[#1d6fd1]"}`}>
                      {plan.price.suffix}
                    </span>
                  </p>
                ) : (
                  <p
                    className={`font-display mt-6 text-lg font-bold ${
                      isPopular ? "text-[#2f9be8]" : "text-[#1d6fd1]"
                    }`}
                    data-testid={`pricing-price-${i}`}
                  >
                    {t.pricing.priceLabel}
                  </p>
                )}
                {plan.price?.eur && (
                  <p
                    className={`mt-1 text-sm font-semibold ${isPopular ? "text-[#94a3b8]" : "text-[#64748b]"}`}
                    data-testid={`pricing-eur-${i}`}
                  >
                    {plan.price.eur}
                  </p>
                )}
                {i === 0 && (
                  <p
                    className="mt-2 text-xs leading-relaxed text-[#94a3b8]"
                    data-testid="pricing-price-note"
                  >
                    {t.pricing.priceNote}
                  </p>
                )}
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          isPopular ? "text-[#2f9be8]" : "text-[#1d6fd1]"
                        }`}
                        strokeWidth={3}
                      />
                      <span className={isPopular ? "text-[#e2e8f0]" : "text-[#334155]"}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className={`mt-8 rounded-none font-display font-bold ${
                    isPopular
                      ? "bg-[#1d6fd1] text-white hover:bg-[#2f9be8]"
                      : "bg-[#0d2b52] text-white hover:bg-[#1d6fd1]"
                  }`}
                  data-testid={`pricing-cta-${i}`}
                >
                  <a href="#contact">{t.pricing.cta}</a>
                </Button>
              </article>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm font-semibold text-[#64748b]" data-testid="pricing-note">
          {t.pricing.note}
        </p>
        <p className="mt-2 text-center text-xs text-[#94a3b8]" data-testid="pricing-note-onrc">
          {t.pricing.note2}
        </p>
      </div>
    </section>
  );
};
